import { Grid, IconButton } from '@mui/material';
import styles from './simulator-v2-dashboard-header.module.scss';
import {
  CcfBox,
  CcfTypography,
  CcfButton,
  CcfAlert,
  CcfSnackBar,
  CcfTooltip,
} from '@nice-ccf/ui-controls';
import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCreateNewJourney,
  getSelectedContact,
  simulatorV2Actions,
} from '../../+state/simulatorV2.slice';
import { useEffect, useState } from 'react';
import SimulatorV2HelpPanel from '../../simulator-v2-help/simulator-v2-help-panel';

/* eslint-disable-next-line */
export interface SimulatorV2DashboardHeaderProps {
  title: string;
  handleSaveJourney: () => Promise<boolean> | void;
  rowData?: any[];
  isEditClick?: boolean;
  handleEditJourney: () => Promise<boolean> | void;
  resetJourney: () => void;
  selectedAction?: string;
  saveCustomWorkspaceHandler?: any;
  archivedRowData?: any[];
}

/**
 * used to show the header of the SimulatorV2Dashboard
 * @param props - SimulatorV2DashboardHeaderProps
 * @example ` <SimulatorV2DashboardHeader />`
 */
export function SimulatorV2DashboardHeader(
  props: SimulatorV2DashboardHeaderProps
) {
  const validationCheck = {
    'Journey Details': '',
    'Customer Details': '',
    Overview: '',
  };
  const {
    handleSaveJourney,
    rowData,
    isEditClick,
    handleEditJourney,
    resetJourney,
    selectedAction,
    saveCustomWorkspaceHandler,
    archivedRowData,
  } = props;
  const dispatch = useDispatch();
  const [showToast, setShowToast] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const { setCreateNewJourney } = simulatorV2Actions;
  const isCreateNewJourney = useSelector(getCreateNewJourney);
  const selectedContact: any = useSelector(getSelectedContact);

  const [errors, setErrors]: any = useState(validationCheck);
  // const [notesErrors, setNotesErrors] = useState<{ [key: string]: string }>({});

  /**
   * createNewJourneyHandler used to create new journey
   * @example <createNewJourneyHandler />
   */
  const handleJourneyAction = async (action: string) => {
    switch (action) {
      case 'Create':
        resetJourney();
        dispatch(setCreateNewJourney(true));
        break;
      case 'Cancel':
        resetJourney();
        setErrors(validationCheck);
        dispatch(setCreateNewJourney(false));
        break;
      case 'Save': {
        // Only leave the editor when the save actually succeeded; a blocked
        // save (e.g. Copilot validation errors) keeps the user on the page.
        const saved = await handleSaveJourney();
        if (saved) dispatch(setCreateNewJourney(false));
        break;
      }
      case 'Edit': {
        const updated = await handleEditJourney();
        if (updated) dispatch(setCreateNewJourney(false));
        break;
      }
      default:
        break;
    }
  };

  /**@example */
  const validateJourneyDetails = () => {
    const newErrors = { ...errors };
    const allData = [
      ...JSON.parse(JSON.stringify(rowData)),
      ...JSON.parse(JSON.stringify(archivedRowData)),
    ];
    // Journey Name is required
    if (
      !selectedContact.journeyName ||
      selectedContact.journeyName.trim() === ''
    ) {
      newErrors['Journey Details'] = 'Journey name is required';
    } else {
      // Check for duplicate journey
      const isDuplicate = allData?.some((row: any) => {
        const normalizedRowName = row.journeyName
          ?.replace(/\s+/g, '')
          .toLowerCase();
        const normalizedSelectedName = selectedContact.journeyName
          .replace(/\s+/g, '')
          .toLowerCase();

        if (!isEditClick) {
          // Creating new journey: check for any duplicate
          return normalizedRowName === normalizedSelectedName;
        } else {
          // Editing: duplicate if journey name changed and matches another row
          // Assume selectedContact has an id or unique identifier
          // If not, fallback to strict name comparison
          return (
            normalizedRowName === normalizedSelectedName &&
            row.id !== selectedContact.id // Prevent matching with itself
          );
        }
      });
      if (isDuplicate) {
        newErrors[
          'Journey Details'
        ] = `Journey "${selectedContact?.journeyName}" already exists in active or archived journeys.`;
      } else {
        // Remove error if resolved
        if (newErrors['Journey Details']) {
          newErrors['Journey Details'] = '';
        }
      }
    }

    // Collect missing fields for Customer Details
    // Check for missing required customer fields
    const requiredCustomerFields = [
      { field: 'firstName', label: 'First name' },
      { field: 'lastName', label: 'Last name' },
      { field: 'emailId', label: 'Email id' },
      // Add more required fields as needed
    ];
    const missingCustomerFields: string[] = [];
    requiredCustomerFields.forEach(({ field, label }) => {
      if (
        !selectedContact.customer[field] ||
        selectedContact.customer[field].trim() === ''
      ) {
        missingCustomerFields.push(label);
      }
    });
    if (missingCustomerFields.length > 0) {
      // Build message like "In customer details firstName and lastName is required"
      const fieldList =
        missingCustomerFields.length === 1
          ? missingCustomerFields[0]
          : missingCustomerFields.slice(0, -1).join(', ') +
            ' and ' +
            missingCustomerFields[missingCustomerFields.length - 1];
      newErrors['Customer Details'] = `${fieldList} is required`;
    } else {
      // Remove error if all fields are resolved
      if (newErrors['Customer Details']) {
        newErrors['Customer Details'] = '';
      }
    }

    // Notes validation
    if (selectedContact.notes && selectedContact.notes.length > 0) {
      const notes = selectedContact.notes;
      const requiredFields = [
        { field: 'noteTitle', label: 'Note title' },
        { field: 'subTitle', label: 'Note subtitle' },
        { field: 'agentName', label: 'Agent name' },
      ];

      // Collect error messages for each note index
      const overviewErrors: { [key: string]: string } = {};
      notes.forEach((note: any, noteIndex: number) => {
        const missingFields: string[] = [];
        requiredFields.forEach(({ field, label }) => {
          if (
            !note?.[field] ||
            (typeof note[field] === 'string' && note[field].trim() === '')
          ) {
            missingFields.push(label);
          }
        });
        if (missingFields.length > 0) {
          overviewErrors[noteIndex] = `Note ${
            noteIndex + 1
          } - ${missingFields.join(', ')} is required`;
        }
      });

      // Remove resolved errors for notes that are now valid
      const prevOverviewErrors = newErrors['Overview'] || {};
      Object.keys(prevOverviewErrors).forEach((key) => {
        if (!overviewErrors[key]) {
          delete prevOverviewErrors[key];
        }
      });

      // Merge new errors
      const mergedOverviewErrors = { ...prevOverviewErrors, ...overviewErrors };

      // If there are errors, set as a single string message (list)
      if (Object.keys(mergedOverviewErrors).length > 0) {
        newErrors['Overview'] = Object.values(mergedOverviewErrors).join('; ');
      } else {
        // If no errors, clear Overview errors
        newErrors['Overview'] = '';
      }
    } else {
      // If no notes, clear Overview errors
      newErrors['Overview'] = '';
    }

    setErrors(newErrors);
    setShowToast(true);
    return newErrors;
  };

  /**@example */
  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowToast(false);
    setErrors(validationCheck);
  };

  return (
    <Grid
      className={styles.dashboardHeaderContainer}
      id="dashboardHeaderContainer"
    >
      <CcfBox className={styles.titleGroup}>
        <CcfTypography className={styles.title}>{props.title}</CcfTypography>
        <CcfTooltip title="Help" arrow>
          <IconButton
            className={styles.helpIcon}
            aria-label="Help"
            onClick={() => setIsHelpOpen(true)}
          >
            <ContactSupportIcon />
          </IconButton>
        </CcfTooltip>
        <SimulatorV2HelpPanel
          open={isHelpOpen}
          onClose={() => setIsHelpOpen(false)}
          activeSectionId={props.title}
        />
      </CcfBox>
      {selectedAction !== 'Archived' && (
        <CcfBox className={styles.btnContainer}>
          {!isCreateNewJourney && selectedAction !== 'Custom Workspace' ? (
            <CcfButton
              className={`${styles.btn} ${styles.saveBtn}`}
              onClick={() => handleJourneyAction('Create')}
            >
              <CcfTypography className={`${styles.btnText} ${styles.txtSave}`}>
                Create New Journey
              </CcfTypography>
            </CcfButton>
          ) : !isCreateNewJourney && selectedAction === 'Custom Workspace' ? (
            <CcfButton
              className={`${styles.btn} ${styles.saveBtn}`}
              onClick={() => {
                saveCustomWorkspaceHandler();
              }}
            >
              <CcfTypography className={`${styles.btnText} ${styles.txtSave}`}>
                Save
              </CcfTypography>
            </CcfButton>
          ) : (
            <>
              <CcfButton
                className={`${styles.btn} ${styles.cancelBtn}`}
                onClick={() => handleJourneyAction('Cancel')}
              >
                <CcfTypography
                  className={`${styles.btnText} ${styles.txtCancel}`}
                >
                  Cancel
                </CcfTypography>
              </CcfButton>
              <CcfButton
                className={`${styles.btn} ${styles.saveBtn}`}
                onClick={() => {
                  const check = validateJourneyDetails();
                  // Only proceed if there are no validation errors
                  if (
                    check &&
                    Object.values(check).every((errorObj: any) => {
                      return !errorObj || Object.keys(errorObj).length === 0;
                    })
                  ) {
                    if (!isEditClick) {
                      handleJourneyAction('Save');
                    } else {
                      handleJourneyAction('Edit');
                    }
                  }
                  // handleJourneyAction('Save');
                }}
              >
                <CcfTypography
                  className={`${styles.btnText} ${styles.txtSave}`}
                >
                  Save
                </CcfTypography>
              </CcfButton>
            </>
          )}
        </CcfBox>
      )}
      {errors &&
        Object.values(errors).some(
          (errorObj: any) => errorObj && Object.keys(errorObj).length > 0
        ) && (
          <CcfBox>
            <CcfSnackBar
              open={showToast}
              // autoHideDuration={3000}
              onClose={() => handleClose()}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <div>
                <CcfAlert
                  closeAlert={handleClose}
                  severity="error"
                  style={{
                    height: 'auto',
                    width: 'auto',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {errors &&
                    Object.entries(errors).map(
                      ([key, value]) =>
                        value &&
                        value !== '' && (
                          <li key={key} className={styles.errorText}>
                            {key}:{' '}
                            {typeof value === 'string'
                              ? value
                              : JSON.stringify(value)}
                          </li>
                        )
                    )}
                </CcfAlert>
              </div>
            </CcfSnackBar>
          </CcfBox>
        )}
    </Grid>
  );
}

export default SimulatorV2DashboardHeader;
